"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Empty, Divider, Tag, Pagination, Spin } from "antd";
import { BookData, getBooksByTag } from "../../utils/apiSample/book-data";
import SearchBar from "./BasicSearch";
import { IBookTypeData } from "../../apiservice/userService.type";
import { userGetBooks, userGetBooksByTag } from "../../apiservice/userService";
import NoAudioBookFound from "../sharedcomponents/NoBookFoundInCategory";

const TagSearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState<IBookTypeData[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [tagName, setTagName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const pageSize = 20;

  // Parse tag from URL query parameters
  useEffect(() => {
    fetchBooks();
    setCurrentPage(1); // Reset to first page when tag changes
  }, [location.search]);

  const fetchBooks = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const tag = params.get("tag");
      setTagName(tag || "");

      const bookData = await userGetBooks({
        tag: tag || "",
        page: 1,
        pageSize,
      });

      if (bookData?.data) {
        setSearchResults(bookData?.data);
        setTotalPage(bookData?.meta?.total);
      } else {
        setError("Books not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }

    setHasSearched(true);
    setCurrentPage(1);
  };

  const fetchBooksPage = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const tag = params.get("tag");
      setTagName(tag || "");

      const bookData = await userGetBooks({
        tag: tag || "",
        page: currentPage,
        pageSize,
      });

      if (bookData?.data) {
        setSearchResults(bookData?.data);
        setTotalPage(bookData?.meta?.total);
      } else {
        setError("Books not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }

    setHasSearched(true);
  };

  useEffect(() => {
    if (hasSearched) {
      fetchBooksPage();
    }
  }, [currentPage]);

  if (loading) {
    // Default text color will be dark:text-stone-100 due to global styles
    return (
      <div className="p-4 text-center text-gray-900 dark:text-stone-100">
        {" "}
        <Spin size="large" /> &nbsp; Loading book details...
      </div>
    );
  }

  if (error || !searchResults.length) {
    return (
      <div className="p-4 text-center text-red-500">
        {error || <NoAudioBookFound></NoAudioBookFound>}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <SearchBar />
      </div>

      <div>
        <Divider orientation="left">
          {tagName ? (
            <span className="text-gray-900 dark:text-stone-100">
              Books tagged with <Tag color="blue">{tagName}</Tag> ({totalPage})
            </span>
          ) : (
            <span className="text-gray-900 dark:text-stone-100">
              Search by tag
            </span>
          )}
        </Divider>

        {searchResults.length > 0 ? (
          <div>
            <div className="grid grid-cols-1 gap-4">
              {searchResults.map((book) => (
                <div
                  key={book.id}
                  onClick={() =>
                    navigate(
                      `/users/book-details/${book.id}/${encodeURIComponent(
                        book.title.replace(/\s+/g, "_")
                      )}`
                    )
                  }
                  className="flex flex-row border border-gray-200 dark:border-stone-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer bg-white dark:bg-stone-800"
                >
                  <div className="flex-grow p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-stone-100">
                      {book.title}
                    </h3>
                    <p className="text-gray-600 dark:text-stone-400">
                      {book.authorName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-stone-500 mt-2 line-clamp-2">
                      {book.summary}
                    </p>

                    <div className="mt-2 flex flex-wrap gap-1">
                      {(book.tags || [])?.map((tag, idx) => (
                        <Tag
                          key={idx}
                          color={tag?.title === tagName ? "blue" : "default"}
                        >
                          {tag?.title}
                        </Tag>
                      ))}
                      {book.isNewRelease && (
                        <Tag color="orange">New Release</Tag>
                      )}
                    </div>
                  </div>
                  <div className="w-1/3 min-w-[120px]">
                    <img
                      alt={book.title}
                      src={book.coverImageUrl || "/placeholder.svg"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-center">
              <Pagination
                current={currentPage}
                onChange={setCurrentPage}
                total={totalPage}
                pageSize={pageSize}
                hideOnSinglePage
              />
            </div>
          </div>
        ) : (
          <Empty
            description={
              tagName
                ? `No books found with tag "${tagName}"`
                : "Select a tag to see books"
            }
          />
        )}
      </div>
    </div>
  );
};

export default TagSearchPage;
